<template>
  <div class="relative">
    <button
      type="button"
      class="relative p-1.5 rounded-xl border border-transparent text-gray-600 transition hover:text-gray-800 hover:border-gray-200 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
      aria-label="Notifications" @click="togglePanel">
      <OverlayBadge
        v-if="unreadCount > 0" :value="unreadCount" severity="danger" :pt="{
          pcBadge: {
            root: {
              class:
                'min-w-[18px] h-[18px] text-[10px] leading-[18px] px-1 rounded-full -top-1 -right-1',
            }
          },
        }">
        <i class="pi pi-bell text-base"/>
      </OverlayBadge>
      <i v-else class="pi pi-bell text-base"/>
    </button>

    <OverlayPanel ref="panelRef" class="shadow-2xl border border-gray-200/70 rounded-2xl">
      <div class="w-[380px] max-w-[85vw] bg-white">
        <div class="flex items-start justify-between gap-4 pb-3 border-b border-gray-100">
          <div class="space-y-0.5">
            <p class="text-sm font-semibold text-gray-900">Notifications</p>
            <p class="text-xs text-gray-500">Alertes et mises à jour récentes</p>
            <p v-if="totalCount > 0" class="text-[11px] text-gray-400">
              {{ totalCount }} notification(s) non lue(s)
            </p>
          </div>
          <button
            type="button"
            class="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            :disabled="unreadCount === 0 || isLoading" @click="markAllAsRead">
            Tout marquer comme lu
          </button>
        </div>

        <div v-if="isLoading && !hasLoaded" class="py-6 text-center text-sm text-gray-500">
          Chargement des notifications...
        </div>

        <div v-else-if="notifications.length === 0" class="py-10 text-center">
          <div
            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <i class="pi pi-check text-lg"/>
          </div>
          <p class="text-sm font-medium text-gray-800">Aucune notification</p>
          <p class="text-xs text-gray-500">Vous êtes à jour.</p>
          <NuxtLink
            to="/notifications"
            class="mt-3 inline-block text-xs font-medium text-primary-600 hover:text-primary-700">
            Voir toutes les notifications
          </NuxtLink>
        </div>

        <div v-else class="pt-3">
          <div class="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            <div
              v-for="notification in notifications" :key="notification.id"
              class="p-3 rounded-xl border border-gray-200 bg-white hover:border-primary-200 hover:bg-primary-50/30 transition">
              <div class="flex items-start gap-3">
                <div
                  class="flex items-center justify-center w-9 h-9 rounded-xl"
                  :class="getSeverityClasses(notification)">
                  <i :class="getIconClass(notification)"/>
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="text-sm font-semibold text-gray-900 truncate">
                        {{ getTitle(notification) }}
                      </p>
                      <p class="text-xs text-gray-500 truncate">
                        {{ getSubtitle(notification) }}
                      </p>
                    </div>
                    <span class="text-[11px] text-gray-400 whitespace-nowrap">
                      {{ formatDate(notification.created_at) }}
                    </span>
                  </div>

                  <p class="mt-1 text-xs text-gray-600">
                    {{ getMessage(notification) }}
                  </p>

                  <div
                    v-if="isDocumentExpired(notification) && documentPreview(notification).items.length"
                    class="mt-2 space-y-1">
                    <div
                      v-for="doc in documentPreview(notification).items" :key="doc.id"
                      class="flex items-center justify-between text-[11px] text-gray-600">
                      <span class="truncate">{{ doc.type }} - {{ doc.name }}</span>
                      <span class="text-gray-400">
                        {{ formatDate(doc.expiry_date) }}
                      </span>
                    </div>
                    <div v-if="documentPreview(notification).remaining > 0" class="text-[11px] text-gray-500">
                      +{{ documentPreview(notification).remaining }} autre(s) document(s)
                    </div>
                  </div>

                  <div class="mt-2 flex items-center gap-3">
                    <button
                      v-if="getPrimaryLink(notification)" type="button"
                      class="text-xs font-medium text-primary-600 hover:text-primary-700"
                      @click="openNotification(notification)">
                      Voir le commerçant
                    </button>
                    <button
                      v-if="!notification.read_at" type="button" class="text-xs text-gray-500 hover:text-gray-700"
                      @click="markAsRead(notification)">
                      Marquer comme lu
                    </button>
                  </div>
                </div>

                <span v-if="!notification.read_at" class="mt-1 inline-flex h-2 w-2 rounded-full bg-red-500"/>
              </div>
            </div>
          </div>

          <div class="pt-3 mt-3 border-t border-gray-100">
            <NuxtLink to="/notifications" class="text-xs font-medium text-primary-600 hover:text-primary-700">
              Voir toutes les notifications
            </NuxtLink>
          </div>
        </div>
      </div>
    </OverlayPanel>
  </div>
</template>

<script setup lang="ts">
import { DateTime } from "luxon";
import OverlayPanel from "primevue/overlaypanel";
import OverlayBadge from "primevue/overlaybadge";
import type { NotificationItem, NotificationDocumentDetail } from "~/types/Notification";

const panelRef = ref<InstanceType<typeof OverlayPanel> | null>(null);
const notifications = ref<NotificationItem[]>([]);
const isLoading = ref(false);
const hasLoaded = ref(false);
const unreadCount = ref(0);
const totalCount = ref(0);
const perPage = 6;

const cityRestricted = inject("cityRestricted", false);
const { fetchNotifications, markNotificationAsRead, markAllNotificationsAsRead } =
  useNotifications();

const refreshNotifications = async () => {
  console.log("refreshNotifications")
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    const response = await fetchNotifications({ per_page: perPage, unread: true }, 1);
    notifications.value = response.data || [];
    unreadCount.value = response.meta?.unread_count || 0;
    totalCount.value = response.meta?.total || 0;
    hasLoaded.value = true;
  } catch (error) {
    console.error("Impossible de charger les notifications:", error);
  } finally {
    isLoading.value = false;
  }
};

const togglePanel = (event: Event) => {
  panelRef.value?.toggle(event);
  refreshNotifications();
};

const markAsRead = async (notification: NotificationItem) => {
  if (notification.read_at) return;
  try {
    await markNotificationAsRead(notification.id);
    notification.read_at = DateTime.now().toISO();
    notifications.value = notifications.value.filter((item) => item.id !== notification.id);
    unreadCount.value = Math.max(0, unreadCount.value - 1);
    totalCount.value = Math.max(0, totalCount.value - 1);
  } catch (error) {
    console.error("Impossible de marquer la notification comme lue:", error);
  }
};

const markAllAsRead = async () => {
  if (unreadCount.value === 0) return;
  try {
    await markAllNotificationsAsRead();
    notifications.value = [];
    unreadCount.value = 0;
    totalCount.value = 0;
  } catch (error) {
    console.error("Impossible de tout marquer comme lu:", error);
  }
};

const openNotification = async (notification: NotificationItem) => {
  await markAsRead(notification);
  const link = getPrimaryLink(notification);
  if (link) {
    panelRef.value?.hide();
    await navigateTo(link);
  }
};

const isDocumentExpired = (notification: NotificationItem) =>
  notification.notification_type === "document_expired";

const documentPreview = (notification: NotificationItem) => {
  const docs = notification.notification_data.documents || [];
  const sorted = [...docs].sort((a, b) => {
    if (!a.expiry_date && !b.expiry_date) return 0;
    if (!a.expiry_date) return 1;
    if (!b.expiry_date) return -1;
    return DateTime.fromISO(a.expiry_date).toMillis() - DateTime.fromISO(b.expiry_date).toMillis();
  });
  return {
    items: sorted.slice(0, 2) as NotificationDocumentDetail[],
    remaining: Math.max(0, sorted.length - 2),
  };
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = DateTime.fromISO(value);
  return date.isValid ? date.toFormat("dd/LL/yyyy") : "-";
};

const getTitle = (notification: NotificationItem) =>
  notification.notification_data.title || "Notification";

const getMessage = (notification: NotificationItem) =>
  notification.notification_data.message || "";

const getSubtitle = (notification: NotificationItem) => {
  const merchant = notification.notification_data.merchant_name || "";
  const market = notification.notification_data.market_name || "";
  if (merchant && market) return `${merchant} - ${market}`;
  return merchant || market || "";
};

const getPrimaryLink = (notification: NotificationItem) => {
  const merchantId = notification.notification_data.merchant_id;
  if (!merchantId) return null;
  return cityRestricted ? `/city/merchants/${merchantId}` : `/merchants/${merchantId}`;
};

const getSeverityClasses = (notification: NotificationItem) => {
  const severity = notification.notification_data.severity;
  if (severity === "error") return "bg-red-100 text-red-700";
  if (severity === "warning") return "bg-amber-100 text-amber-700";
  if (severity === "info") return "bg-blue-100 text-blue-700";
  return "bg-gray-100 text-gray-600";
};

const getIconClass = (notification: NotificationItem) => {
  const icon = notification.notification_data.icon;
  if (icon === "alert-circle") return "pi pi-exclamation-circle";
  if (icon === "alert-triangle") return "pi pi-exclamation-triangle";
  if (icon === "file-warning") return "pi pi-file";
  return "pi pi-bell";
};

const { $eventBus } = useNuxtApp();

onMounted(() => {
  refreshNotifications();

  // Écouter l'événement de rafraîchissement des notifications depuis FCM
  $eventBus.on("notifications:refresh", refreshNotifications);
});

onBeforeUnmount(() => {
  $eventBus.off("notifications:refresh", refreshNotifications);
});
</script>
